// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import SettingsInputComponentRoundedIcon from '@mui/icons-material/SettingsInputComponentRounded';

// material ui
import {
  alpha,
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

export default function TargetsSelector({
  title,
  buildings, // [{ buildingId, buildingName, subs: [{id, name}] }]
  selectedBuildings, // Set<id>
  selectedSubs, // Set<id>
  onToggleBuilding, // (b) => void
  onToggleSub, // (b, subId) => void
}) {
  const { t } = useTranslation('calendar');
  const defaultTitle = t('selectTargets');
  const isBuildingChecked = (b) => selectedBuildings.has(b.buildingId);
  const countSelectedSubs = (b) =>
    b.subs.reduce((acc, s) => acc + (selectedSubs.has(s.id) ? 1 : 0), 0);

  // Per-building collapse state; default open if any sub is selected
  const initialOpen = useMemo(() => {
    const map = new Map();
    for (const b of buildings) {
      const checked = selectedBuildings.has(b.buildingId);
      const selectedCount = b.subs.reduce(
        (acc, s) => acc + (selectedSubs.has(s.id) ? 1 : 0),
        0,
      );
      map.set(b.buildingId, selectedCount > 0 || checked);
    }
    return map;
  }, [buildings, selectedSubs, selectedBuildings]);

  const [openMap, setOpenMap] = useState(initialOpen);
  const toggleOpen = (id) =>
    setOpenMap((prev) => new Map(prev).set(id, !prev.get(id)));

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        {title || defaultTitle}
      </Typography>

      <Stack gap={1.25}>
        {buildings.map((b) => {
          const checked = isBuildingChecked(b);
          const rawSelectedCount = countSelectedSubs(b);
          const hasSubs = b.subs.length > 0;

          // Display logic mirrors ViewFilterSelector, but "valgt" instead of "vist"
          const selectedCountDisplay = checked
            ? b.subs.length
            : rawSelectedCount;
          const allSubsSelected = hasSubs && rawSelectedCount === b.subs.length;
          const showCheckBadge = checked || allSubsSelected;

          const partial =
            !showCheckBadge &&
            rawSelectedCount > 0 &&
            rawSelectedCount < b.subs.length;

          const isOpen = openMap.get(b.buildingId) ?? false;

          return (
            <Card
              key={b.buildingId}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Building row */}
              <CardActionArea
                onClick={() => onToggleBuilding(b)}
                aria-pressed={checked}
                sx={{
                  px: 1.25,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 0,
                  gap: 2,
                  ...(checked && {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                  }),
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    showCheckBadge ? (
                      <CheckCircleRoundedIcon
                        color="success"
                        fontSize="small"
                      />
                    ) : partial ? (
                      <RadioButtonUncheckedRoundedIcon fontSize="small" />
                    ) : null
                  }
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: (t) =>
                        partial
                          ? t.palette.warning.main
                          : checked
                            ? 'white'
                            : 'white',
                      color: (t) =>
                        partial || checked ? t.palette.common.white : 'inherit',
                      width: 20,
                      height: 20,
                    },
                  }}
                >
                  <ApartmentRoundedIcon
                    color={checked ? 'primary' : 'action'}
                  />
                </Badge>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {b.buildingName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                  >
                    {hasSubs &&
                      !checked &&
                      `${selectedCountDisplay}/${b.subs.length} blandesløjfer valgt`}
                    {!hasSubs && 'Ingen blandesløjfer'}
                    {checked &&
                      `${b.subs.length}/${b.subs.length} blandesløjfer valgt`}
                  </Typography>
                </Box>

                {/* Expand/Collapse (does not toggle building) */}
                {hasSubs && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOpen(b.buildingId);
                    }}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </CardActionArea>

              {/* Subscriptions — collapsible dense list */}
              {hasSubs && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Divider />
                  <CardContent sx={{ py: 0.5 }}>
                    <List dense disablePadding>
                      {b.subs.map((s) => {
                        const subSelected = checked || selectedSubs.has(s.id);
                        const disabled = checked; // lock when building is selected

                        const row = (
                          <ListItemButton
                            key={s.id}
                            dense
                            disableGutters
                            disabled={disabled}
                            onClick={
                              disabled ? undefined : () => onToggleSub(b, s.id)
                            }
                            sx={{
                              px: 1,
                              borderRadius: 1.5,
                              my: 1,
                              ...(subSelected && {
                                bgcolor: (t) =>
                                  alpha(t.palette.primary.main, 0.08),
                              }),
                              ...(disabled && { cursor: 'not-allowed' }),
                              transition: 'background-color 0.15s ease-in-out',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <SettingsInputComponentRoundedIcon
                                fontSize="small"
                                color={subSelected ? 'primary' : 'disabled'}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={s.name}
                              slotProps={{
                                primary: {
                                  variant: 'body2',
                                  noWrap: true,
                                }
                              }}
                            />
                            {subSelected ? (
                              <CheckCircleRoundedIcon
                                fontSize="small"
                                color="success"
                              />
                            ) : (
                              <RadioButtonUncheckedRoundedIcon
                                fontSize="small"
                                color="disabled"
                              />
                            )}
                          </ListItemButton>
                        );

                        return disabled ? (
                          <Tooltip
                            key={s.id}
                            title={
                              t?.('tooltips.mixingLoopsLockedSelected') ||
                              "Alle blandesløjfer er låst til 'valgt', fordi bygningen er valgt"
                            }
                            placement="left"
                            arrow
                          >
                            <span>{row}</span>
                          </Tooltip>
                        ) : (
                          row
                        );
                      })}
                    </List>
                  </CardContent>
                </Collapse>
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

